// This is the DraftsTab component, which will display the drafts for the user and allow for the user to edit dels as needed.

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  parseHomeRoute,
  buildCanonicalHomePath,
} from "../../../../services/RouteUtils";
import { shallow } from "zustand/shallow";
import { copyDel } from "../../../../api/DelApi";
import { getLocalDateString } from "../../../../utils/dateUtils";
import { useAppStore } from "../../../../store/appStore";
import { useUserState } from "../../../../context/user";
import { useFilters } from "../../../../hooks/useFilters";
import { useFilterMenu } from "../../../../hooks/useFilterMenu";
import { filtersInitialState } from "../../../../store/shared/filters/filtersState";
// Components
import DelForm from "../../../delForm/DelForm";
import {
  NewDelButton,
  EditDraftButton,
  CopyDelButton,
  TransferDelButton,
  RefreshDelListsButton,
  FilterButton,
} from "../buttons/DelActions";
import { DelFilterMenu } from "../../../common/FilterMenu";
import { DelDraftTable } from "../del_table";
import TransferDelModal from "../modal/TransferDelModal";
import ConfirmationModal from "../../../common/ConfirmationModal";
import styles from "./DraftsTab.module.css";

export const DraftsTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // User Info
  const user = useUserState().currentUser;
  // Employees
  const employees = useAppStore((s) => s.employees.list, shallow);
  // Terminated employees
  const allTerminatedEmployees = useAppStore(
    (s) => s.employees.terminatedList,
    shallow,
  );
  const allSearchEmployees = [...employees, ...allTerminatedEmployees];
  // Projects
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  // Inactive projects
  const allInactiveProjects = useAppStore(
    (s) => s.projects.inactiveList,
    shallow,
  );
  // Loading UI state
  const { isActive } = useAppStore((s) => s.loading, shallow);
  // Loading UI actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  // Notification UI actions
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow,
  );
  // Drafts List State
  const drafts = useAppStore((s) => s.delLists.drafts.items, shallow);
  // console.log("drafts", drafts);
  // console.log("delLists", useAppStore.getState().delLists);
  const totalCount = useAppStore((s) => s.delLists.drafts.totalCount, shallow);
  const currentPage = useAppStore(
    (s) => s.delLists.drafts.currentPage,
    shallow,
  );
  const selectedDraft = useAppStore(
    (s) => s.delLists.drafts.selectedDraft,
    shallow,
  );
  const pageSize = useAppStore((s) => s.delLists.drafts.pageSize, shallow);

  // DEL UI States
  const { showDelForm, showTransferDelModal } = useAppStore(
    (s) => s.draftsTabUI,
    shallow,
  );
  const activeDel = useAppStore((s) => s.activeDelDrafts, shallow);
  const draftDelActions = useAppStore((s) => s.activeDelDraftsActions, shallow);
  const hydrateDraftFromPayload = useAppStore(
    (s) => s.activeDelDraftsActions.hydrateDelFromPayload,
  );

  // App Store Actions
  // Workflow Actions
  const deleteDraftDel = useAppStore(
    (s) => s.delWorkflowActions.deleteDraftDel,
  );
  const transferDel = useAppStore((s) => s.delWorkflowActions.transferDel);

  // Specific DEL data actions
  const startNewDel = useAppStore((s) => s.activeDelDraftsActions.startNewDel);
  const fetchDel = useAppStore((s) => s.activeDelDraftsActions.fetchDel);

  // Specific DEL UI actions
  const setShowDelForm = useAppStore(
    (s) => s.draftsTabUIActions.setShowDelForm,
  );
  const setShowTransferDelModal = useAppStore(
    (s) => s.draftsTabUIActions.setShowTransferDelModal,
  );

  // Handling Page UI actions
  const pageSizeChange = useAppStore(
    (s) => s.delListsActions.setDraftsPageSize,
  );
  const setDraftsPage = useAppStore((s) => s.delListsActions.setDraftsPage);
  const setSubmitsPage = useAppStore((s) => s.delListsActions.setSubmitsPage);
  const selectDraftRow = useAppStore((s) => s.delListsActions.setSelectedDraft);
  // Refreshing DEL Lists, both Drafts and Submits.
  const refreshDraftsDelLists = useAppStore(
    (s) => s.delListsActions.fetchDraftsPage,
  );
  const refreshSubmitsDelLists = useAppStore(
    (s) => s.delListsActions.fetchSubmitsPage,
  );

  // Local filter state (resets on unmount)
  const {
    filters: draftsFilters,
    updateFilter: updateFilterAction,
    clearFilters: clearFiltersAction,
  } = useFilters({ status: "0" });
  // Filter menu UI state
  const {
    showFilterMenu,
    toggleFilterMenu: handleToggleFilterMenu,
    filterPhaseList,
    setFilterPhaseList: filterPhaseActionFunction,
    filterMenuRef,
  } = useFilterMenu();

  // Delete confirmation modal state (holds the DEL id pending deletion, or null)
  // We use a custom modal instead of window.confirm because Safari on tablets
  // lets users block native confirm dialogs, which breaks deletion entirely.
  const [pendingDeleteDelId, setPendingDeleteDelId] = useState(null);

  const handleApplyFilters = async () => {
    showLoading("Loading DELs...");
    setDraftsPage({ currentPage: 1, pageSize });
    await refreshDraftsDelLists(user.employee_id, draftsFilters);
    hideLoading();
  };
  const handleClearFilters = async () => {
    clearFiltersAction();
    setDraftsPage({ currentPage: 1, pageSize });
    showLoading("Loading DELs...");
    await refreshDraftsDelLists(
      user.employee_id,
      filtersInitialState({ status: "0" }),
    );
    hideLoading();
  };

  // Specific DelForm handlers
  const handleShowNewDel = async () => {
    const target = buildCanonicalHomePath({
      tab: "drafts",
      action: "new",
      open: true,
    });
    navigate(target);
  };

  const handleShowDelDraft = async () => {
    // If user hasn't selected a del, we need to alert them.
    if (!selectedDraft) {
      window.alert("Please select a DEL to edit.");
      return;
    }
    const target = buildCanonicalHomePath({
      tab: "drafts",
      delId: selectedDraft.id,
      action: "edit",
      open: true,
    });
    navigate(target);
  };

  // Fetchs a Draft DEL to edit, same as above.
  const handleDoubleClick = async (del) => {
    // If user double clicks on a del, we need to handle the double click
    selectDraftRow(del);
    const target = buildCanonicalHomePath({
      tab: "drafts",
      delId: del.id,
      action: "edit",
      open: true,
    });
    navigate(target);
  };

  const handleCopyDel = async () => {
    if (!selectedDraft) {
      window.alert("Please select a DEL to copy.");
      return;
    }
    try {
      showLoading("Copying DEL...");
      // Create the copy on the server synchronously
      const resp = await copyDel(selectedDraft.id, user, "drafts");
      const { del_id, del_creation_date, summary } = resp || {};
      if (!del_id) {
        throw new Error("Copy failed");
      }
      // Upsert into drafts list immediately so refresh/backout isn't blank
      if (summary) {
        useAppStore.getState().delListsActions.upsertDraftLocal(summary);
      } else {
        // Fallback minimal row
        useAppStore.getState().delListsActions.upsertDraftLocal({
          id: del_id,
          del_creation_date: del_creation_date || getLocalDateString(),
          updated: getLocalDateString(),
          project_number: "-",
        });
      }
      // Hydrate active draft directly from the copy payload to avoid a second fetch
      if (resp && resp.del_info) {
        await hydrateDraftFromPayload("edit", resp.del_info);
        setShowDelForm(true);
        const target = buildCanonicalHomePath({
          tab: "drafts",
          delId: del_id,
          action: "edit",
          open: true,
        });
        navigate(target);
      } else {
        // Fallback: navigate and let deep-link fetch if payload missing
        const target = buildCanonicalHomePath({
          tab: "drafts",
          delId: del_id,
          action: "edit",
          open: true,
        });
        navigate(target);
      }
      hideLoading();
    } catch (error) {
      hideLoading();
      window.alert("Error copying DEL, please try again later.");
      console.error("Error copying DEL:", error);
    }
  };

  // Handlers for transfer of DELS
  // This will be a local state item, so keep the state local.
  const handleShowTransferDelModal = async () => {
    if (!selectedDraft) {
      window.alert("Please select a DEL to transfer.");
      return;
    } else {
      setShowTransferDelModal(true);
    }
  };

  const handleTransferDel = async (employeeId) => {
    try {
      await transferDel(selectedDraft.id, employeeId, user.employee_id, user);
    } catch (error) {
      console.error("Error transferring DEL:", error);
    }
  };

  // Handlers for pagination
  // Handle change in page size
  const handlePageSizeChange = async (e) => {
    showLoading("Loading DELs...");
    const newPageSize = parseInt(e.target.value, 10);
    pageSizeChange(newPageSize);
    await refreshDraftsDelLists(user.employee_id, draftsFilters);
    hideLoading();
  };

  const handlePageChange = async (direction) => {
    showLoading("Loading DELs...");

    let newPage;
    if (direction === "next") {
      newPage = currentPage + 1;
    } else {
      newPage = currentPage - 1;
    }
    setDraftsPage({ currentPage: newPage, pageSize });
    await refreshDraftsDelLists(user.employee_id, draftsFilters);
    hideLoading();
  };

  // Open the delete confirmation modal for the given DEL id
  const handleDeleteDel = (delId) => {
    setPendingDeleteDelId(delId);
  };

  // Close the delete confirmation modal without deleting
  const handleCancelDelete = () => {
    setPendingDeleteDelId(null);
  };

  // Perform the deletion after the user confirms in the modal
  const handleConfirmDelete = async () => {
    const delId = pendingDeleteDelId;
    setPendingDeleteDelId(null);
    if (!delId) {
      return;
    }
    try {
      showLoading("Deleting DEL...");
      await deleteDraftDel(delId, user);
      // Fetch the new page after deletion completes
      await refreshDraftsDelLists(user.employee_id, draftsFilters);
      hideLoading();
    } catch (error) {
      hideLoading();
      showNotification({
        type: "error",
        title: "Error Deleting DEL",
        message: "Please try again later.",
      });
      console.error("Error deleting DEL:", error);
    } finally {
      hideLoading();
    }
  };

  const handleSelectDraftRow = (del) => {
    selectDraftRow(del);
  };

  // Refresh handler
  const handleRefreshDelLists = async () => {
    showLoading("Refreshing DELs...");
    setDraftsPage({ currentPage: 1, pageSize });
    const submitsPageSize = useAppStore.getState().delLists.submits.pageSize;
    setSubmitsPage({ currentPage: 1, pageSize: submitsPageSize });
    await refreshDraftsDelLists(user.employee_id, draftsFilters);
    await refreshSubmitsDelLists(user.employee_id);
    hideLoading();
  };

  // Deep-link handler (single source of truth)
  const processedRef = useRef(null);
  useEffect(() => {
    const run = async () => {
      const parsed = parseHomeRoute(location.pathname, location.search);
      if (parsed.tab !== "drafts") return;
      const idStr = parsed.delId;
      const action = parsed.action || (idStr ? "edit" : null);
      const wantsOpen = !!parsed.open;
      const key = `drafts:${action || ""}:${idStr || ""}:${
        wantsOpen ? "1" : "0"
      }`;
      if (processedRef.current === key) return;
      processedRef.current = key;

      try {
        // If URL points to list view (no del_id, no action), ensure form is closed
        if (!idStr && !action) {
          if (showDelForm) setShowDelForm(false);
          return;
        }

        // Guard: If already open on the same draft in edit mode, skip re-fetch
        if (
          idStr &&
          wantsOpen &&
          action === "edit" &&
          showDelForm &&
          useAppStore.getState().activeDelDrafts?.currentDel?.id &&
          String(useAppStore.getState().activeDelDrafts.currentDel.id) ===
            String(idStr)
        ) {
          return;
        }

        if (action === "new" && wantsOpen) {
          // Close form first to prevent rendering stale/null data during fetch
          if (showDelForm) setShowDelForm(false);
          showLoading("Starting new DEL...");
          await startNewDel(user);
          setShowDelForm(true);

          // After creating new DEL, update URL to edit mode with del_id
          // This prevents refresh from creating another new DEL
          const newDelId =
            useAppStore.getState().activeDelDrafts.currentDel?.id;
          if (newDelId) {
            const editTarget = buildCanonicalHomePath({
              tab: "drafts",
              delId: newDelId,
              action: "edit",
              open: true,
            });
            navigate(editTarget, { replace: true });
          }
        } else if (idStr && wantsOpen && action === "edit") {
          // Close form first to prevent rendering stale/null data during fetch
          if (showDelForm) setShowDelForm(false);
          showLoading("Loading DEL...");

          const res = await fetchDel("edit", idStr);
          if (res && res.ok) {
            console.log("DEL loaded successfully");
            setShowDelForm(true);
          } else {
            setShowDelForm(false);
            try {
              useAppStore.getState().notificationActions.showNotification({
                type: "error",
                title: "Error Loading DEL",
                message: "DEL not found or could not be loaded.",
              });
              console.error("Error loading DEL:", res);
            } catch {}
          }
        } else if (idStr && !wantsOpen) {
          selectDraftRow({ id: idStr });
          setShowDelForm(false);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to process Drafts deep link", err);
      } finally {
        hideLoading();
      }
    };
    run();
  }, [
    location.pathname,
    location.search,
    startNewDel,
    fetchDel,
    selectDraftRow,
    setShowDelForm,
    showLoading,
    hideLoading,
    user,
  ]);

  return (
    <>
      {showDelForm ? (
        <>
          <DelForm
            activeDelTabInfo={activeDel}
            actionFunctions={draftDelActions}
            setShowDelForm={() => setShowDelForm(false)}
            tableName="drafts"
            refreshDelListFunction={handleRefreshDelLists}
          />
        </>
      ) : (
        <>
          <div className={styles.tableActions}>
            <NewDelButton newDelFunction={handleShowNewDel} />
            <EditDraftButton editFunction={handleShowDelDraft} />
            <CopyDelButton copyFunction={handleCopyDel} />
            <TransferDelButton
              showTransferDelModal={handleShowTransferDelModal}
            />
          </div>
          {showTransferDelModal && (
            <TransferDelModal
              onTransferFunction={handleTransferDel}
              onCancel={() => setShowTransferDelModal(false)}
              employees={employees}
              afterTransfer={() => setShowTransferDelModal(false)}
            />
          )}

          <ConfirmationModal
            isOpen={pendingDeleteDelId !== null}
            onCancel={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            title="Delete DEL"
            message="Are you sure you want to delete this DEL? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            confirmButtonClass="deleteButton"
          />

          <div className={styles.headerWithRefresh}>
            <h2>Draft DELs</h2>
            <div style={{ position: "relative" }} ref={filterMenuRef}>
              <FilterButton filterFunction={handleToggleFilterMenu} />
              {showFilterMenu && (
                <DelFilterMenu
                  delTab="drafts"
                  filters={draftsFilters}
                  updateFilterAction={updateFilterAction}
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={handleClearFilters}
                  allProjectInfo={allProjectInfo}
                  allInactiveProjects={allInactiveProjects}
                  allEmployees={allSearchEmployees}
                  filterPhaseList={filterPhaseList}
                  filterPhaseActionFunction={filterPhaseActionFunction}
                />
              )}
            </div>
            <RefreshDelListsButton
              refreshFunction={handleRefreshDelLists}
              isLoading={isActive}
            />
          </div>

          <div className={styles.tableWrapper}>
            {drafts.length > 0 ? (
              <DelDraftTable
                dels={drafts.slice(0, pageSize)}
                onRowSelect={handleSelectDraftRow}
                onDoubleClick={handleDoubleClick}
                onDelete={handleDeleteDel}
                selectedDelId={selectedDraft?.id || null}
              />
            ) : (
              <p className={styles.noData}>No draft DELs to display</p>
            )}
          </div>
          <div className={styles.paginationControls}>
            <div className={styles.paginationLeftControls}>
              <button
                onClick={() => handlePageChange("prev")}
                disabled={currentPage === 1 || isActive}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange("next")}
                disabled={currentPage * pageSize >= totalCount || isActive}
              >
                Next
              </button>
              <div className={styles.pageSizeSelector}>
                <label htmlFor="pageSize">Rows:</label>
                <select
                  id="pageSize"
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
            <span>
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} -{" "}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
              drafts
            </span>
          </div>
        </>
      )}
    </>
  );
};
